import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SkeletonCardLoading from '../../components/Shared/SkeletonCardLoading';
import { getERPClient } from '../../services/supplyChainApi';
import FilterComponent from '../../components/home/FilterComponent';
import toast from 'react-hot-toast';
import { FaMoneyBillWave, FaFileInvoice, FaBoxOpen, FaTruck } from 'react-icons/fa';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const MetricCard = ({ title, value, icon, subTitle, currency }) => (
  <div className="bg-white rounded-md border border-gray-200 shadow-sm p-4 h-full">
    <div className="flex items-center gap-3">
      <div className="bg-gray-100 w-[40px] h-[40px] flex items-center justify-center rounded-full text-[#0070C2] text-xl">
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500 font-medium">{title}</p>
        <h3 className="text-xl font-bold text-gray-800">
          {value} {currency}
        </h3>
        {subTitle && <p className="text-xs text-gray-400">{subTitle}</p>}
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-md shadow-sm h-[350px]">
    <div className="p-4 border-b border-gray-100">
      <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="w-full h-[280px] pt-4 pr-4">{children}</div>
  </div>
);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

export default function ERPPurchasingStats() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const getOneMonthAgo = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  };

  const getToday = () => {
    return new Date().toISOString().split('T')[0];
  };

  const [filterDate, setFilterDate] = useState({
    start_date: getOneMonthAgo(),
    end_date: getToday(),
  });

  const [stats, setStats] = useState({
    totalPurchaseValue: 0,
    invoiceCount: 0,
    totalGrnValue: 0,
    totalQtyDispatched: 0,
    stockReceivedByMonth: [],
    receivedByItem: [],
    dispatchByDepartment: [],
  });

  const fetchStats = async () => {
    setLoading(true);
    try {
      const erpClient = getERPClient();
      const params = {};
      if (filterDate.start_date) params.start_date = filterDate.start_date;
      if (filterDate.end_date) params.end_date = filterDate.end_date;

      const res = await erpClient.getPurchasingStats(params);
      const data = res?.data || {};
      const purchase = data.purchase || {};
      const stock = data.stock_received || {};
      const dispatch = data.dispatch || {};

      let receivedItems = Array.isArray(data.received_by_item) ? data.received_by_item.slice(0, 5) : [];
      let dispatchDepts = Array.isArray(data.dispatch_by_department) ? data.dispatch_by_department : [];

      setStats({
        totalPurchaseValue: Number(purchase.total_purchase_value || 0),
        invoiceCount: Number(purchase.invoice_count || 0),
        totalGrnValue: Number(stock.total_value_received || 0),
        totalQtyDispatched: Number(dispatch.total_quantity_dispatched || 0),
        stockReceivedByMonth: Array.isArray(stock.by_month) ? stock.by_month : [],
        receivedByItem: receivedItems,
        dispatchByDepartment: dispatchDepts,
      });
    } catch (err) {
      console.error('Failed to load ERP purchasing stats:', err);
      toast.error(t('Failed to load purchasing stats'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleUpdateFilters = async () => {
    fetchStats();
  };

  const tooltipStyle = {
    backgroundColor: '#fff',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  };

  return (
    <div className="space-y-5 min-h-screen">

      <div className="flex flex-col md:flex-row justify-end items-center mb-3">
        {/* <h2 className="text-xl font-bold text-gray-800 ml-2">{t('Purchasing Dashboard')}</h2> */}
        <div className="flex justify-end items-center">
          <FilterComponent
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            handleUpdateFilters={handleUpdateFilters}
            loading={loading}
          />
        </div>
      </div>

      {loading ? (
        <SkeletonCardLoading rows={3} />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricCard
              title={t('Total Purchase Value')}
              value={stats.totalPurchaseValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              icon={<FaMoneyBillWave />}
              currency="SAR"
            />
            {/* <MetricCard
              title={t('Total GRN Value')}
              value={stats.totalGrnValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              icon={<FaBoxOpen />}
              currency="SAR"
            /> */}
            <MetricCard
              title={t('Invoice Count')}
              value={stats.invoiceCount.toLocaleString()}
              icon={<FaFileInvoice />}
              currency=""
            />
            <MetricCard
              title={t('Items Dispatched')}
              value={stats.totalQtyDispatched.toLocaleString()}
              icon={<FaTruck />}
              currency="Units"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title={t('Stock Received Trend (Value)')}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.stockReceivedByMonth}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(val) => val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(value) => `SAR ${Number(value).toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="value_received" name={t('Value Received')} fill="#50C878" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={t('Top 5 Items Received (Qty)')}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.receivedByItem} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="item_name" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                  <Bar dataKey="quantity_received" name={t('Received')} fill="#0096FF" radius={[0, 4, 4, 0]} barSize={15} />
                  <Bar dataKey="quantity_accepted" name={t('Accepted')} fill="#F4BB44" radius={[0, 4, 4, 0]} barSize={15} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={t('Dispatch Distribution by Department (Qty)')}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.dispatchByDepartment}
                    dataKey="quantity_dispatched"
                    nameKey="department_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, percent }) => percent > 0.05 ? `${name.substring(0, 10)}...` : ''}
                  >
                    {stats.dispatchByDepartment.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={t('Top Departments by Dispatch Quantity')}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[...stats.dispatchByDepartment].sort((a, b) => b.quantity_dispatched - a.quantity_dispatched).slice(0, 5)}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="department_name" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                  <Bar dataKey="quantity_dispatched" name={t('Qty Dispatched')} fill="#DE3163" radius={[4, 4, 0, 0]} barSize={25} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}
